import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

type FAQItem = { q: string; a: string };
type Category = { id: string; label: string; icon: string; items: FAQItem[] };

const categories: Category[] = [
  {
    id: "shipping",
    label: "Shipping",
    icon: "ri-truck-line",
    items: [
      {
        q: "How long does shipping take?",
        a: "Standard orders are processed within 1–3 business days. Once shipped, standard delivery takes 5–10 business days. Freight items (large or heavy generators) typically arrive within 7–14 business days. Delivery times may vary based on your location and product type.",
      },
      {
        q: "What shipping carriers do you use?",
        a: "We ship via UPS and FedEx for most orders. Large, heavy, or oversized items are shipped via freight carriers. You'll receive carrier and tracking details by email once your order ships.",
      },
      {
        q: "Do you ship outside the United States?",
        a: "At this time, Iron Haven Supply ships within the United States only. We do not currently offer international shipping.",
      },
      {
        q: "Can you ship to a P.O. Box?",
        a: "P.O. Box delivery is not available for large or freight-class items due to carrier restrictions. Standard-size accessories and smaller products may qualify for P.O. Box delivery — contact us before ordering if you need to confirm.",
      },
      {
        q: "How do I track my order?",
        a: "As soon as your order ships, you'll receive a tracking notification email with your carrier and tracking number. You can use that number directly on the carrier's website (UPS.com or FedEx.com) to follow your shipment in real time.",
      },
      {
        q: "What if my item arrives damaged?",
        a: "If your product arrives damaged, please document it with photos immediately and contact us at support@ironhavensupply.com within 48 hours of delivery. We'll arrange a replacement or full refund at no cost to you, including return shipping.",
      },
      {
        q: "Is Iron Haven Supply responsible for carrier delays?",
        a: "While we work with reliable carriers, we are not responsible for delays caused by carrier issues, weather events, holidays, or supply chain disruptions. If your order is significantly delayed, contact us and we'll work with the carrier on your behalf.",
      },
    ],
  },
  {
    id: "returns",
    label: "Returns & Refunds",
    icon: "ri-refresh-line",
    items: [
      {
        q: "What is your return window?",
        a: "We accept returns within 30 days of delivery. After 30 days, items are no longer eligible for return unless covered under a manufacturer warranty.",
      },
      {
        q: "How do I start a return?",
        a: "Email us at support@ironhavensupply.com with your order number and the reason for your return. We'll respond within 1–2 business days with return instructions and a Return Merchandise Authorization (RMA) number. Do not ship items back without an RMA — they cannot be processed.",
      },
      {
        q: "What items are eligible for return?",
        a: "Items must be unused, in new condition, and in their original packaging. Products that show signs of use, installation, or damage not caused during shipping are not eligible. Custom or special-order items and clearance items are final sale and cannot be returned.",
      },
      {
        q: "Who pays for return shipping?",
        a: "If the return is due to a defective product or a shipping error on our part, Iron Haven Supply covers the full cost of return shipping. If you're returning for any other reason (change of mind, ordered wrong item, etc.), return shipping costs are the customer's responsibility.",
      },
      {
        q: "Is there a restocking fee?",
        a: "A restocking fee of up to 25% may apply to standard returns, particularly for large or freight-class items. This fee covers inspection, repackaging, and logistics handling. Restocking fees do not apply to defective or incorrectly shipped items.",
      },
      {
        q: "How long does a refund take?",
        a: "Once we receive and inspect your return, refunds are processed within 5–10 business days. The refund is issued to your original payment method. Depending on your bank or card provider, it may take an additional 3–5 business days to appear on your statement.",
      },
      {
        q: "What if I received the wrong item?",
        a: "Contact us immediately at support@ironhavensupply.com with your order number and photos of what you received. We'll ship the correct item at no additional charge and arrange free pickup of the incorrect product.",
      },
    ],
  },
  {
    id: "warranty",
    label: "Warranty",
    icon: "ri-shield-check-line",
    items: [
      {
        q: "What warranty coverage comes with products?",
        a: "All products sold by Iron Haven Supply are covered by the manufacturer's warranty. Warranty terms vary by brand and product type — typical coverage ranges from 1 to 5 years on components, and some manufacturers offer extended battery warranties of up to 10 years. Check the individual product listing for warranty details.",
      },
      {
        q: "How do I file a warranty claim?",
        a: "Contact us at support@ironhavensupply.com with your order number, a description of the issue, and photos or video if possible. We'll help you determine whether the issue is covered and coordinate with the manufacturer on your behalf. Most warranty claims are resolved within 5–10 business days.",
      },
      {
        q: "Does warranty cover accidental damage?",
        a: "Standard manufacturer warranties cover defects in materials and workmanship — they do not cover accidental damage, misuse, improper installation, or environmental damage (e.g., flood, lightning strike). If you're concerned about accidental damage, we recommend looking into homeowner's insurance or a third-party protection plan.",
      },
      {
        q: "What voids a warranty?",
        a: "Warranties are typically voided by unauthorized modifications, using the product outside of its rated specifications, using non-approved accessories or components, improper installation, and physical damage caused by the owner. Always follow the manufacturer's installation and usage guidelines.",
      },
      {
        q: "Can I transfer a warranty if I sell the product?",
        a: "Warranty transferability depends on the manufacturer. Some brands allow one-time warranty transfers to a new owner, while others limit coverage to the original purchaser. Contact us or the manufacturer directly with the product serial number to confirm transfer eligibility.",
      },
    ],
  },
  {
    id: "orders",
    label: "Orders & Products",
    icon: "ri-shopping-bag-line",
    items: [
      {
        q: "Can I cancel my order after placing it?",
        a: "Orders can be cancelled within 2 hours of placement, provided processing has not yet begun. After that window, cancellation may not be possible. To request a cancellation, call us immediately at (866) 605-4975 or email support@ironhavensupply.com with your order number.",
      },
      {
        q: "Can I change my order after placing it?",
        a: "Minor changes (like correcting a shipping address) may be possible within the first 2 hours. Changes to the products ordered are generally not possible once an order is confirmed. Contact us right away and we'll do our best to accommodate you before the order enters fulfillment.",
      },
      {
        q: "Why was my order cancelled?",
        a: "Orders may be cancelled due to payment failure, suspected fraud, a product going out of stock after your order was placed, or an inability to verify your shipping address. If your order was cancelled, you'll receive an email explaining why and a full refund if payment was collected.",
      },
      {
        q: "Do you restock sold-out items?",
        a: "Yes — most products are restocked regularly. If an item you want is out of stock, check back within 1–2 weeks or contact us at support@ironhavensupply.com to ask about restock timelines or comparable alternatives.",
      },
      {
        q: "How do I choose the right backup power system for my home?",
        a: "The right system depends on your essential load (what you need to power), how long you need coverage, and whether you want solar recharging capability. A rough guide: portable systems (1–3kWh) cover phones, lights, and a refrigerator for a day; home backup systems (5–15kWh) can run most essentials for 1–3 days; whole-home standby systems handle your entire panel indefinitely with proper fuel or solar input. Contact our team and we can help size the right system for your situation.",
      },
      {
        q: "Are your products compatible with solar panels?",
        a: "Many of our systems support solar input — compatible products will list their maximum solar input wattage and connector type (typically MC4 or XT60). We recommend pairing systems with solar panels sized to fully recharge the battery in 4–8 hours of good sunlight. Contact us if you need help matching panels to a specific system.",
      },
    ],
  },
  {
    id: "payments",
    label: "Payments",
    icon: "ri-bank-card-line",
    items: [
      {
        q: "What payment methods do you accept?",
        a: "We accept Visa, Mastercard, American Express, Discover, Shop Pay (with optional installments), Apple Pay, and Google Pay. All payment options available will be displayed at checkout.",
      },
      {
        q: "Is it safe to enter my card information on your site?",
        a: "Yes. Iron Haven Supply processes all payments through a PCI-DSS compliant payment gateway. We do not store your full credit card number on our servers at any time. All checkout data is encrypted using industry-standard SSL/TLS.",
      },
      {
        q: "Do you offer financing or buy-now-pay-later?",
        a: "Shop Pay Installments may be available at checkout for qualifying orders, allowing you to split your purchase into interest-free payments. Eligibility is determined by Shop Pay at checkout. Additional financing options may be introduced in the future.",
      },
      {
        q: "Why was my payment declined?",
        a: "Payments can be declined for several reasons: insufficient funds, incorrect billing address, card issuer security hold, or our fraud screening system flagging the transaction. Try re-entering your billing info, confirm the address matches your card on file, or contact your bank. If the problem persists, try an alternate payment method or contact us.",
      },
      {
        q: "Will I be charged sales tax?",
        a: "Sales tax is calculated automatically at checkout based on your shipping address and applicable state and local tax laws. The tax amount will be displayed before you confirm your order.",
      },
    ],
  },
];

const BASE_URL = "https://ironhavensupply.com";

const schemaData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      "@id": `${BASE_URL}/faq#breadcrumb`,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": BASE_URL,
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "FAQ",
          "item": `${BASE_URL}/faq`,
        },
      ],
    },
    {
      "@type": ["WebPage", "FAQPage"],
      "@id": `${BASE_URL}/faq#webpage`,
      "url": `${BASE_URL}/faq`,
      "name": "Frequently Asked Questions | Iron Haven Supply",
      "description": "Answers to common questions about shipping, returns, warranties, orders, and payments at Iron Haven Supply — backup power systems for home and business.",
      "isPartOf": { "@id": `${BASE_URL}/#website` },
      "about": { "@id": `${BASE_URL}/#business` },
      "breadcrumb": { "@id": `${BASE_URL}/faq#breadcrumb` },
      "mainEntity": categories.flatMap((cat) =>
        cat.items.map((item) => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.a,
          },
        }))
      ),
    },
  ],
};

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState("shipping");
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const current = categories.find((c) => c.id === activeCategory) ?? categories[0];

  const handleCategoryChange = (id: string) => {
    setActiveCategory(id);
    setOpenIndex(0);
  };

  return (
    <main className="font-sans bg-white min-h-screen">
      {/* Schema.org FAQPage structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <Navbar />

      {/* Hero */}
      <div className="pt-20 bg-gray-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-10 pb-12">
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-6">
            <Link to="/" className="hover:text-amber-500 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-300"></i>
            <span className="text-gray-700 font-medium">FAQ</span>
          </nav>

          <div className="flex items-start gap-4">
            <div className="w-11 h-11 flex items-center justify-center bg-amber-100 rounded-xl shrink-0">
              <i className="ri-questionnaire-line text-2xl text-amber-500"></i>
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight">
                Frequently Asked Questions
              </h1>
              <p className="text-gray-500 mt-2 text-base max-w-xl">
                Everything you need to know about shipping, returns, warranties, and orders — answered clearly, no runaround.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Sidebar category nav */}
          <aside className="lg:w-52 shrink-0">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">Categories</p>
            <nav className="flex flex-row lg:flex-col gap-1.5 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-left transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.id
                      ? "bg-amber-500 text-white"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${cat.icon} text-base`}></i>
                  </div>
                  {cat.label}
                  <span className={`ml-auto text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                    activeCategory === cat.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-400"
                  }`}>
                    {cat.items.length}
                  </span>
                </button>
              ))}
            </nav>

            {/* Contact CTA */}
            <div className="mt-8 hidden lg:block bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg mb-3">
                <i className="ri-customer-service-2-line text-lg text-amber-500"></i>
              </div>
              <p className="text-sm font-bold text-gray-900 mb-1">Still need help?</p>
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">Our team is available Mon–Fri, 9am–5pm EST.</p>
              <Link
                to="/contact"
                className="block text-center bg-gray-900 hover:bg-gray-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Contact Support
              </Link>
            </div>
          </aside>

          {/* FAQ accordion */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-5 h-5 flex items-center justify-center text-amber-500">
                <i className={`${current.icon} text-lg`}></i>
              </div>
              <h2 className="text-lg font-extrabold text-gray-900">{current.label}</h2>
              <span className="text-xs text-gray-400 font-medium">{current.items.length} questions</span>
            </div>

            <div className="space-y-2">
              {current.items.map((item, i) => (
                <div
                  key={i}
                  className={`border rounded-xl overflow-hidden transition-all ${
                    openIndex === i ? "border-amber-200 bg-amber-50/40" : "border-gray-200 bg-white"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <span className={`font-semibold text-sm pr-4 leading-snug ${openIndex === i ? "text-amber-700" : "text-gray-900"}`}>
                      {item.q}
                    </span>
                    <div className={`w-6 h-6 flex items-center justify-center shrink-0 rounded-full transition-colors ${
                      openIndex === i ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-500"
                    }`}>
                      <i className={`text-sm transition-transform duration-200 ${openIndex === i ? "ri-subtract-line" : "ri-add-line"}`}></i>
                    </div>
                  </button>

                  {openIndex === i && (
                    <div className="px-5 pb-5">
                      <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile contact CTA */}
            <div className="mt-8 lg:hidden bg-gray-50 border border-gray-200 rounded-xl p-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-gray-900">Still have questions?</p>
                <p className="text-xs text-gray-500 mt-0.5">Mon–Fri, 9am–5pm EST</p>
              </div>
              <Link
                to="/contact"
                className="bg-gray-900 hover:bg-gray-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-14 bg-gray-900 rounded-2xl px-6 md:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="text-center md:text-left">
            <p className="text-white font-extrabold text-lg">Can't find the answer you need?</p>
            <p className="text-gray-400 text-sm mt-1">
              Reach out directly — we respond within 1 business day.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <a
              href="tel:8666054975"
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm text-amber-400"></i>
              </div>
              (866) 605-4975
            </a>
            <a
              href="mailto:support@ironhavensupply.com"
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line text-sm"></i>
              </div>
              Email Support
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
